import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import "./ThesisPage.css";

const ThesisPage: React.FC = () => {
  return (
    <div className="thesis-page">
      <div className="thesis-header">
        <Link to="/" className="back-link">
          <ArrowLeft size={20} />
          back to home
        </Link>
      </div>

      <div className="thesis-content">
        <h1>why eth is the better hold than btc for the next decade</h1>

        <div className="thesis-section">
          <h2>the one line</h2>
          <p>
            eth is evolving into the settlement and application layer of the
            internet itself while btc remains primarily a monetary asset. that
            difference in surface area for real world use is widening because of
            ethereum's roadmap, its developer base, and the way value flows
            through the protocol when people actually use it.
          </p>
        </div>

        <div className="thesis-section">
          <h2>the core of the case</h2>

          <h3>
            1. ethereum has the broadest programmable surface in crypto and the
            deepest builder community
          </h3>
          <p>
            bitcoin is money first. ethereum is money plus a general purpose
            computing platform. that platform hosts stablecoins, tokenized
            treasuries and funds, defi, identity, social, gaming, and more.
            builders follow that surface area. electric capital's developer
            report tracks ethereum as the top ecosystem by active developers and
            long term contributors, reflecting a sustained lead in tooling and
            mindshare. this is not a hype cycle point. it is a capacity point.
            platforms with more makers ship more things.
          </p>
          <p>
            recent institutional onchain launches show this in practice.
            blackrock's first tokenized fund buidl lives on ethereum and crossed
            the billion mark in assets months after launch, and the tokenized
            treasury market has continued to expand on ethereum since then.
            fidelity rolled out a tokenized money market fund share class on
            ethereum in september twenty twenty five. these are sober, regulated
            products choosing the same base.
          </p>

          <h3>
            2. ethereum is becoming the settlement layer for its own internet
            scale rollups
          </h3>
          <p>
            most user activity now happens on layer two networks that inherit
            security from ethereum. after the dencun upgrade with eip 4844 in
            march twenty twenty four, rollups post compressed data to ethereum
            using blob space, which cut posting costs and pushed a surge in
            activity. the foundation itself notes order of magnitude fee
            reductions for rollups, and l2beat's activity dashboard shows
            aggregate rollup throughput now far above layer one. this is the
            internet stack forming in front of us.
          </p>
          <p>
            this is not just traffic. it is value flow back to eth. rollups
            settle to ethereum and pay fees in eth for data availability. blobs
            are an eth native resource. pectra went live in may twenty twenty
            five and doubled average blob throughput again via eip 7691, and
            explicitly frames the path to further increases and toward data
            availability sampling. more rollup space means more reasons to use
            the base chain, and that circles back to the asset used to pay base
            chain costs.
          </p>

          <h3>
            3. ethereum's monetary plumbing connects usage to the asset through
            fees and burn
          </h3>
          <p>
            since the london upgrade with eip 1559 the base fee from every
            transaction in eth is destroyed. that binds network use to supply
            dynamics in a direct way that is unique among major chains. after
            the merge, issuance dropped sharply versus the old proof of work
            schedule. neither of these require any price view to matter. they
            just change the plumbing so that activity has a measurable onchain
            effect on supply.
          </p>

          <h3>
            4. the roadmap keeps shipping useful things without breaking the
            social contract
          </h3>
          <p>
            ethereum has a track record of major planned upgrades that deliver
            concrete user benefits while preserving decentralization through a
            multi client culture. there is no single reference client. several
            independent teams maintain both execution and consensus clients, by
            design, to reduce correlated failure risk. that matters over a
            decade horizon.
          </p>
          <p>
            on the protocol side, the community is actively formalizing proposer
            builder separation to keep the mev supply chain competitive and
            reduce centralization risk, with work toward enshrined pbs captured
            in the research and eip process. in other words, the chain
            acknowledges real world market structure and iterates to make it
            healthier.
          </p>

          <h3>
            5. policy and market access tailwinds are lining up the same way
            they did for btc, and they target eth the utility asset
          </h3>
          <p>
            spot eth exchange traded products launched in the united states in
            july twenty twenty four after the sec approved exchange rule changes
            in may. that created a clean, regulated wrapper for institutions
            that cannot hold native assets. btc had this first, but now both
            assets have it. the key difference is that eth's wrapper tracks an
            asset people need to actually use applications and settlement on a
            live computing network.
          </p>

          <h3>6. energy and esg fit is no longer a debate for ethereum</h3>
          <p>
            after the merge, estimated energy use fell by about ninety nine
            point nine percent. the ethereum foundation cites ccri's study
            showing annual electricity consumption in the range of a few
            gigawatt hours for the whole network. for many institutions, that
            removes a blocker that will not be removed for proof of work assets.
            it is not a moral point. it is a mandate compliance point.
          </p>

          <h3>
            7. stable money and tokenization want standards and composability.
            ethereum already has that
          </h3>
          <p>
            stablecoins and tokenized funds want predictable standards, deep
            wallets, and interop with apps. defi and wallet infra on ethereum
            and its rollups provide that. blackrock chose ethereum for buidl.
            fidelity just shipped on ethereum. the regulatory perimeter is not
            shrinking. as it grows, the default will continue to be the chain
            with the broadest standards and the most production grade infra.
          </p>

          <h3>8. contrast the change profile</h3>
          <p>
            bitcoin's culture increasingly favors ossification, meaning minimal
            change at the base layer. that is a valid design choice for a
            monetary good. it is just a different product. the current debate in
            the bitcoin community openly uses the word ossification. ethereum,
            in contrast, continues to ship upgrades like dencun and pectra to
            scale while preserving the core rules people value. if you want
            exposure to a system that will adapt to new uses over a decade, that
            is a vote for eth.
          </p>
        </div>

        <div className="thesis-section">
          <h2>supporting details that tie the story together</h2>

          <h3>what the rollup centric architecture means for eth the asset</h3>
          <p>
            layer two ecosystems like base and arbitrum process the bulk of
            transactions today. l2beat's activity page shows base peaking near
            the mid one hundreds in user operations per second, with aggregate
            rollup throughput an order of magnitude above mainnet. those rollups
            buy blob space on ethereum blocks. blob space is priced in eth.
            pectra increased blob capacity and the roadmap points to data
            availability sampling to scale it further. that feedback loop is
            structural and does not depend on any price assumption.
          </p>

          <h3>why developer depth is not a vanity metric</h3>
          <p>
            developer depth predicts speed of iteration and quality of tooling.
            electric capital's ecosystem dashboard tracks ethereum as the
            largest community of active developers, which is consistent with the
            pace and breadth of shipped standards like erc 4337 for account
            abstraction and the move toward wallet features that feel like
            modern apps. pectra's eip 7702 brings those smart account
            capabilities directly to everyday accounts, which directly reduces
            user friction for the next wave of apps.
          </p>

          <h3>why institutions building onchain picked ethereum first</h3>
          <p>
            we now have real names choosing public chains for production, not
            pilots. blackrock's buidl launched on ethereum in march twenty
            twenty four and crossed one billion in assets by march twenty twenty
            five. fidelity followed with a tokenized money market fund on
            ethereum in september twenty twenty five. that is not a narrative.
            it is a choice repeated by multiple firms with deep compliance
            stacks.
          </p>

          <h3>why the energy profile matters over a decade</h3>
          <p>
            many asset owners have explicit exclusion lists or internal hurdles
            tied to energy and emissions. ethereum's move to proof of stake
            removed that hurdle in a way that is measurable and independently
            studied. bitcoin's proof of work can and does use renewables, but
            the policy optics differ. that matters for who can hold what and at
            what size.
          </p>
        </div>

        <div className="thesis-section">
          <h2>risks and how the roadmap addresses them</h2>
          <p>
            mev and builder concentration are real risks. academic work shows
            that a handful of builders have at times produced a large share of
            mev boost blocks, highlighting centralization pressure in block
            building. the community response is to push proposer builder
            separation into the protocol, which reduces reliance on trusted
            relays and hardens the market. this is the right kind of reflex for
            a long hold. name the risk, then ship the fix.
          </p>
          <p>
            rollup decentralization is work in progress. l2beat's stages
            framework tracks concrete decentralization milestones for rollups.
            some networks are already stage one and moving up. that public
            scoreboard and the cultural pressure to reach stage two are unique
            advantages of building under a common security and standards
            umbrella.
          </p>
          <p>
            base layer client diversity must stay healthy. ethereum's multi
            client design makes the network resilient, and the community
            actively tracks client share and advocates diversification. that
            cultural muscle memory lowers the tail risk of single client bugs.
          </p>
        </div>

        <div className="thesis-section">
          <h2>a clean summary</h2>
          <p>
            btc is the apex monetary asset in crypto and will likely stay that
            way. if your only goal is a non sovereign store of value, that is a
            fine reason to hold btc. but if you want an asset aligned with
            growing onchain use by people and institutions, eth has the stronger
            non price case over the next decade
          </p>
          <p>it runs the richest application platform with the most builders</p>
          <p>
            it is the security and settlement layer for a whole internet of
            rollups that already process the majority of transactions and pay
            eth for data availability
          </p>
          <p>
            its protocol ties usage to the asset through fee burn and a post
            merge issuance schedule that removed old proof of work inflation
            pressure
          </p>
          <p>
            it keeps shipping upgrades that improve user experience and
            scalability while preserving decentralization through client
            diversity and a serious research culture around mev and block
            building
          </p>
          <p>
            and the largest asset managers on earth are already putting
            regulated products and tokenized funds on ethereum today
          </p>
          <p>
            that is the thesis. no performance promises. just the structure of
            the network and where the real world is actually choosing to build.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ThesisPage;
